import { Link } from 'react-router-dom';

const updatedAt = 'May 22, 2026';

export function PrivacyPage() {
  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-4xl px-5 py-10 sm:py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-squishy-primary hover:underline">
          Back to Squishy School
        </Link>

        <header className="mt-6 rounded-2xl border bg-white p-6 shadow-sm sm:p-8">
          <p className="text-sm font-semibold uppercase tracking-wide text-squishy-primary">Privacy Policy</p>
          <h1 className="mt-2 text-3xl font-bold text-squishy-dark sm:text-4xl">Squishy School Privacy Policy</h1>
          <p className="mt-3 text-sm text-slate-500">Last updated: {updatedAt}</p>
          <p className="mt-5 leading-7 text-slate-700">
            Squishy School is operated by SignOut LLC ("Squishy School," "we," "us," or "our"). This Privacy Policy explains how we collect, use, disclose, and protect information when you use Squishy School on the web, iOS, Android, or as a progressive web app.
          </p>
        </header>

        <div className="mt-6 space-y-5">
          <PolicySection title="Summary">
            <p>
              We collect the information needed to create accounts, generate study questions, save progress, support community features, process subscriptions, keep the app secure, and improve Squishy School. We do not sell personal information, and we do not share personal information for cross-context behavioral advertising or targeted advertising.
            </p>
          </PolicySection>

          <PolicySection title="Information We Collect">
            <ul>
              <li><strong>Account information:</strong> name, email address, login identifiers, display name, profile settings, school or class selections, avatar, subscription tier, and account status.</li>
              <li><strong>Study and learning activity:</strong> enrolled classes or exams, sessions, questions answered, selected answers, correctness, XP, streaks, favorites, comments, votes, reports, and progress data.</li>
              <li><strong>User content:</strong> notes, documents, images, PDFs, URLs, audio, text, prompts, comments, bug reports, support messages, and other material you upload or enter to create or study questions.</li>
              <li><strong>Generated content:</strong> AI-generated questions, explanations, Pro explanations, citations, validation results, and related metadata created from your materials or public exam-bank content.</li>
              <li><strong>Device and usage data:</strong> device type, operating system, app version, browser, IP address, log data, session state, feature usage, upload status, error reports, and similar diagnostics.</li>
              <li><strong>Payment and subscription data:</strong> subscription status, product identifiers, renewal state, entitlement status, store transaction identifiers, and receipts or purchase metadata provided by Apple, Google Play, RevenueCat, or other payment processors. We do not receive full credit card numbers from app-store purchases.</li>
              <li><strong>Camera, photo, microphone, notification, and file access:</strong> only when you choose to grant permission or use features that need those permissions, such as uploading notes, taking photos of study material, recording audio study material, or receiving notifications.</li>
            </ul>
          </PolicySection>

          <PolicySection title="How We Use Information">
            <ul>
              <li>Provide, maintain, personalize, and improve Squishy School.</li>
              <li>Create accounts, authenticate users, prevent unauthorized account access, and enforce one-session or security controls.</li>
              <li>Upload, parse, summarize, and transform study material into questions and explanations.</li>
              <li>Save study progress, resume sessions, calculate accuracy, XP, streaks, question statistics, and leaderboards.</li>
              <li>Operate comments, discussions, reports, moderation, admin tools, and support workflows.</li>
              <li>Process subscriptions, free trials, entitlements, renewals, cancellations, and purchase restoration.</li>
              <li>Monitor app health, debug errors, prevent abuse, detect fraud, secure the service, and comply with law.</li>
              <li>Send service messages, account notices, support replies, and optional notifications.</li>
            </ul>
          </PolicySection>

          <PolicySection title="AI Processing">
            <p>
              When you ask Squishy School to generate questions, explanations, summaries, or study material, the content you provide may be processed by AI service providers such as Google Gemini and Anthropic. We use these providers to produce, validate, and improve study content. Do not upload information you are not allowed to share or information that you do not want processed for study generation.
            </p>
          </PolicySection>

          <PolicySection title="How We Disclose Information">
            <ul>
              <li><strong>Service providers:</strong> vendors that host, secure, process, analyze, or support the app, including AWS, Neon, Apple, Google Play, RevenueCat, payment processors, AI providers, and support or communication tools.</li>
              <li><strong>Community features:</strong> display names, comments, votes, question discussions, and leaderboard information may be visible to other users depending on your settings and the feature used.</li>
              <li><strong>Schools or classes:</strong> class enrollment, study activity, or generated content may be associated with a class, school, exam, or community bank when you choose those features.</li>
              <li><strong>Legal and safety:</strong> when required by law, legal process, security needs, fraud prevention, protection of rights, or enforcement of our terms and policies.</li>
              <li><strong>Business transfers:</strong> if we are involved in a merger, acquisition, financing, reorganization, or sale of assets, information may be transferred as part of that transaction.</li>
            </ul>
          </PolicySection>

          <PolicySection title="No Sale or Targeted Advertising Sharing">
            <p>
              We do not sell personal information. We do not share personal information for cross-context behavioral advertising, and we do not process personal information for targeted advertising. If this changes, we will update this policy and provide any legally required opt-out choices before using information that way.
            </p>
          </PolicySection>

          <PolicySection title="Data Retention">
            <p>
              We keep information for as long as needed to provide Squishy School, maintain your account and study history, comply with legal obligations, resolve disputes, enforce agreements, and protect the service. Raw uploaded files may be stored temporarily for processing and are configured for limited retention. Generated questions, study progress, comments, and account records may remain until deleted, anonymized, or no longer needed. You may request deletion as described below.
            </p>
          </PolicySection>

          <PolicySection title="Security">
            <p>
              We use reasonable administrative, technical, and organizational safeguards designed to protect information, including access controls, encrypted transport, cloud security controls, and limited access to production systems. No online service can guarantee absolute security, so please use a strong password and protect your account credentials.
            </p>
          </PolicySection>

          <PolicySection title="Your Choices">
            <ul>
              <li>Update account profile information in the app.</li>
              <li>Manage or cancel subscriptions through the Apple App Store or Google Play account that started the subscription.</li>
              <li>Change device permissions for camera, photos, microphone, notifications, and files in your device settings.</li>
              <li>Contact us to request access, correction, deletion, portability, or other privacy help.</li>
            </ul>
          </PolicySection>

          <PolicySection title="California Privacy Notice">
            <p>
              California residents may have rights under the California Consumer Privacy Act, as amended by the California Privacy Rights Act, including the right to know/access personal information, receive portable copies, correct inaccurate information, delete personal information, limit certain uses of sensitive personal information, opt out of sale or sharing, and not be discriminated against for exercising privacy rights.
            </p>
            <p>
              In the past 12 months, we may have collected the categories listed in "Information We Collect" above, including identifiers, account information, internet or electronic network activity, education or study-related information you provide, inferences related to study performance, commercial subscription information, and user-generated content. We use and disclose those categories for the purposes listed in this policy. We do not sell or share personal information as those terms are used under California law.
            </p>
            <p>
              You may submit a California privacy request by emailing us at <a href="mailto:support@squishyschool.com">support@squishyschool.com</a>. We may need to verify your identity before completing a request. You may also use an authorized agent where permitted by law.
            </p>
          </PolicySection>

          <PolicySection title="Montana Privacy Notice">
            <p>
              Montana residents may have rights under the Montana Consumer Data Privacy Act, including the right to confirm whether we process personal data, access personal data, correct inaccuracies, delete personal data, obtain a portable copy, opt out of targeted advertising, sale of personal data, or certain profiling, and appeal a denied request.
            </p>
            <p>
              We do not sell personal data, process personal data for targeted advertising, or use personal data for decisions that produce legal or similarly significant effects without providing required rights. To exercise Montana privacy rights or appeal a decision, email <a href="mailto:support@squishyschool.com">support@squishyschool.com</a> and include "Montana Privacy Request" or "Privacy Appeal" in the subject line.
            </p>
          </PolicySection>

          <PolicySection title="European Economic Area, United Kingdom, and Switzerland (GDPR)">
            <p>
              If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you have rights under the General Data Protection Regulation (GDPR) and equivalent laws. SignOut LLC acts as the data controller for your personal data.
            </p>
            <p><strong>Legal bases for processing:</strong></p>
            <ul>
              <li><strong>Contract performance:</strong> processing necessary to provide accounts, study features, subscriptions, and the Service.</li>
              <li><strong>Legitimate interests:</strong> improving the Service, preventing fraud, ensuring security, and operating community features, balanced against your rights and freedoms.</li>
              <li><strong>Consent:</strong> where you have given specific consent, such as for optional notifications or certain cookie uses. You may withdraw consent at any time.</li>
              <li><strong>Legal obligation:</strong> processing required to comply with applicable laws.</li>
            </ul>
            <p><strong>Your GDPR rights:</strong></p>
            <ul>
              <li>Right of access to your personal data.</li>
              <li>Right to rectification of inaccurate data.</li>
              <li>Right to erasure ("right to be forgotten").</li>
              <li>Right to restriction of processing.</li>
              <li>Right to data portability in a structured, commonly used, machine-readable format.</li>
              <li>Right to object to processing based on legitimate interests, including profiling.</li>
              <li>Right to withdraw consent at any time without affecting the lawfulness of prior processing.</li>
              <li>Right to lodge a complaint with a supervisory authority in your country of residence.</li>
            </ul>
            <p>
              To exercise these rights, contact <a href="mailto:support@squishyschool.com">support@squishyschool.com</a> with "GDPR Request" in the subject line. We will respond within 30 days.
            </p>
            <p>
              <strong>International data transfers:</strong> Your data may be transferred to and processed in the United States and other countries where our service providers operate. We rely on Standard Contractual Clauses approved by the European Commission and other lawful transfer mechanisms to protect your data when transferred outside the EEA.
            </p>
          </PolicySection>

          <PolicySection title="Canada (PIPEDA)">
            <p>
              If you are located in Canada, your personal information is protected under the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy legislation. You have the right to:
            </p>
            <ul>
              <li>Access your personal information held by us.</li>
              <li>Request correction of inaccurate or incomplete personal information.</li>
              <li>Withdraw consent to the collection, use, or disclosure of your personal information, subject to legal or contractual restrictions.</li>
              <li>File a complaint with the Office of the Privacy Commissioner of Canada if you believe your privacy rights have been violated.</li>
            </ul>
            <p>
              We collect, use, and disclose personal information only for purposes that a reasonable person would consider appropriate in the circumstances. To exercise your rights, contact <a href="mailto:support@squishyschool.com">support@squishyschool.com</a> with "Canada Privacy Request" in the subject line.
            </p>
          </PolicySection>

          <PolicySection title="Children">
            <p>
              Squishy School is not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child under 13 provided personal information, contact us so we can take appropriate action.
            </p>
          </PolicySection>

          <PolicySection title="International Users">
            <p>
              Squishy School is operated from the United States. If you use the app from outside the United States, your information may be processed and stored in the United States and other locations where our service providers operate.
            </p>
          </PolicySection>

          <PolicySection title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we make material changes, we will update the date above and provide additional notice when required by law or store policy.
            </p>
          </PolicySection>

          <PolicySection title="Contact Us">
            <p>
              SignOut LLC operates Squishy School. For privacy questions or requests, contact us at <a href="mailto:support@squishyschool.com">support@squishyschool.com</a>.
            </p>
          </PolicySection>
        </div>
      </div>
    </main>
  );
}

function PolicySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border bg-white p-6 shadow-sm">
      <h2 className="text-xl font-bold text-squishy-dark">{title}</h2>
      <div className="privacy-copy mt-3 space-y-3 text-sm leading-7 text-slate-700">
        {children}
      </div>
    </section>
  );
}
